/**
 * The server's refusal codes, mirrored from `AuthFailure` in
 * `server/src/oauth.ts`.
 *
 * `session_expired` means signing in again works. `not_invited` means it never
 * will, so a client that retries on it loops through sign-in forever.
 */
export type AuthFailureCode = "session_expired" | "not_invited" | "forbidden_origin" | "account_changed";

const CODES: readonly string[] = ["session_expired", "not_invited", "forbidden_origin", "account_changed"];

export function isAuthFailureCode(value: unknown): value is AuthFailureCode {
  return typeof value === "string" && CODES.includes(value);
}

/** A refusal the client can branch on without reading the prose. */
export class AuthError extends Error {
  readonly code: AuthFailureCode;

  constructor(code: AuthFailureCode, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

/**
 * Reads `{error:{code,message}}` off a refused response.
 *
 * Returns undefined when the response is not an authentication refusal, so
 * callers keep their own error for anything else. A 401 or 403 without a
 * recognizable code still yields an AuthError: the status is enough to know the
 * credential was refused, and `session_expired` is the recoverable assumption.
 */
export async function readAuthError(response: Response): Promise<AuthError | undefined> {
  if (response.status !== 401 && response.status !== 403) return undefined;
  const payload = await response.clone().json().catch(() => undefined) as
    { readonly error?: { readonly code?: unknown; readonly message?: unknown } } | undefined;
  const code = isAuthFailureCode(payload?.error?.code)
    ? payload.error.code
    : response.status === 403 ? "forbidden_origin" : "session_expired";
  const message = typeof payload?.error?.message === "string" && payload.error.message
    ? payload.error.message
    : defaultMessage(code);
  return new AuthError(code, message);
}

export function defaultMessage(code: AuthFailureCode): string {
  switch (code) {
    case "not_invited": return "This address is not invited to RecallVault.";
    case "forbidden_origin": return "That request did not come from the RecallVault app origin.";
    case "session_expired": return "Your RecallVault session is no longer valid.";
    case "account_changed": return "The active RecallVault account changed. Sign in again.";
  }
}
